import { DropdownRenderV2 } from "#src/commonV2/antdHelpers/DropdownRender";
import { BreadcrumbV2 } from "#src/commonV2/Breadcrumb";
import { ButtonV2 } from "#src/commonV2/Button";
import { useFilter } from "#src/commonV2/Table/Filter/useFilter";
import { SearchboxV2 } from "#src/commonV2/Table/Searchbox";
import { TableActionLabelV2 } from "#src/commonV2/Table/TableActionLabel";
import { ConfirmDeleteThreatPreventionPolicyModal } from "#src/components/endpoint/threatPrevention/ConfirmDeleteModal";
import {
  controlDirectionFilterItems,
  controlStatusFilterItems,
  ipActionFilterItems,
} from "#src/components/endpoint/threatPrevention/filterItems";
import { pathname } from "#src/config/pathname";
import useFetchNoPaging from "#src/hooks/useFetchNoPaging";
import { IThreatPreventionPolicyItem } from "#src/services/endpoint/devices/types";
import { endpointService } from "#src/services/endpoint";
import { Dropdown, Switch } from "@lockerpm/design";
import { DotsVertical, Edit02, Plus, Trash03 } from "@untitled-ui/icons-react";
import { ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useParams } from "react-router-dom";
import {
  ControlDirection,
  ThreatPolicyAction,
} from "#src/services/endpoint/threatPrevention/enums";
import { BadgeV2 } from "#src/commonV2/badges/Badge";
import { BadgeSeeMoreV2 } from "#src/commonV2/badges/BadgeSeeMore";
import { apiErrorHandler } from "#src/utils/apiErrorHandler";
import { tagService } from "#src/services/tags";
import { AddIpPolicyModal } from "#src/components/endpoint/threatPrevention/ips/AddIpPolicyModal";
import { EditIpPolicyModal } from "#src/components/endpoint/threatPrevention/ips/EditIpPolicyModal";
import { createToast } from "#src/common/system/toasts";
import { TableDndV2 } from "#src/commonV2/Table/variants/DragNDrop";
import { DeviceTagType } from "#src/services/endpoint/enums";
import { FilterDropdownV2 } from "#src/commonV2/Table/Filter/FilterDropdown";

enum SearchParamKey {
  ACTION_TYPE = "action",
  CONTROL_DIRECTIONS = "directions",
  APPLIED_TAGS = "tags",
  CONTROL_STATUS = "enabled",
}

export const EndpointThreatPreventionIps = () => {
  const { t } = useTranslation("endpoint", { keyPrefix: "threatPrevention" });

  const { workspaceId } = useParams<"workspaceId">();

  if (!workspaceId) {
    return null;
  }

  const fetchTagParams = useMemo<
    Parameters<typeof tagService.listTagsNoPaging>
  >(() => [workspaceId, { q: undefined }], [workspaceId]);

  const { list: tagList, isLoading: isLoadingTag } = useFetchNoPaging(
    tagService.listTagsNoPaging,
    fetchTagParams
  );

  // --------------- DATA ---------------

  const [openAddPolicy, setOpenAddPolicy] = useState<boolean>(false);

  const [editingItem, setEditingItem] =
    useState<IThreatPreventionPolicyItem | null>(null);
  const [confirmDeleteItem, setConfirmDeleteItem] =
    useState<IThreatPreventionPolicyItem | null>(null);

  // Search/sort
  const [searchKeyword, setSearchKeyword] = useState<string>("");

  const { value: controlAction, renderProps: actionFilterRenderProps } =
    useFilter({
      label: t("table.filter.action.label"),
      selectionList: ipActionFilterItems,
      options: {
        searchParamKey: SearchParamKey.ACTION_TYPE,
      },
    });

  const {
    value: controlDirections,
    renderProps: controlDirectionsFilterRenderProps,
  } = useFilter({
    label: t("table.filter.directions.label"),
    selectionList: controlDirectionFilterItems,
    options: {
      searchParamKey: SearchParamKey.CONTROL_DIRECTIONS,
    },
    multiple: true,
  });

  const { value: appliedTags, renderProps: appliedTagsFilterRenderProps } =
    useFilter({
      label: t("table.filter.appliedTags.label"),
      selectionList: tagList
        ? tagList.map((item) => ({
            key: item.id.toString(),
            value: item.id.toString(),
            getLabel: () => item.name,
          }))
        : [],
      options: {
        searchParamKey: SearchParamKey.APPLIED_TAGS,
        itemLabel: "badge",
      },
      isLoading: isLoadingTag,
      multiple: true,
    });

  const { value: controlStatus, renderProps: controlStatusFilterRenderProps } =
    useFilter({
      label: t("table.filter.status.label"),
      selectionList: controlStatusFilterItems,
      options: {
        searchParamKey: SearchParamKey.CONTROL_STATUS,
      },
    });

  // --------------- COMPUTED ---------------

  const fetchParams = useMemo<
    Parameters<typeof endpointService.listThreatPoliciesNoPaging>
  >(
    () => [
      workspaceId,
      {
        q: searchKeyword,
        action: controlAction
          ? controlAction
          : [ThreatPolicyAction.ALLOW_IP, ThreatPolicyAction.BLOCK_IP].join(
              ","
            ),
        enabled: controlStatus,
        group: undefined,
        host_ids: undefined,
        tag_ids: appliedTags,
        source: controlDirections,
      },
    ],
    [
      workspaceId,
      searchKeyword,
      controlAction,
      controlStatus,
      appliedTags,
      controlDirections,
    ]
  );

  // --------------- HOOKS ---------------

  const {
    list: threatPolicyList,
    isLoading,
    updateData: updateThreatPolicyList,
  } = useFetchNoPaging(endpointService.listThreatPoliciesNoPaging, fetchParams);

  // --------------- METHODS ---------------

  // Action list for kebab menu
  const getTableActions = (record: IThreatPreventionPolicyItem) => {
    const actions: {
      key: string;
      label: ReactNode;
      action: () => void;
    }[] = [
      {
        key: "edit",
        label: (
          <TableActionLabelV2
            icon={<Edit02 className="h-5 w-5" />}
            text={t("table.action.edit")}
          />
        ),
        action: () => {
          setEditingItem(record);
        },
      },
      {
        key: "delete",
        label: (
          <TableActionLabelV2
            icon={<Trash03 className="h-5 w-5" />}
            text={t("table.action.delete")}
            warning
          />
        ),
        action: () => {
          setConfirmDeleteItem(record);
        },
      },
    ];

    return actions;
  };

  const onToggleStatus = (item: IThreatPreventionPolicyItem) => {
    endpointService
      .updateEnabledThreatPolicy(workspaceId, item.id, {
        enabled: !item.enabled,
      })
      .then(() => {
        updateThreatPolicyList();
      })
      .catch(apiErrorHandler);
  };

  const onSwapPriority = (
    draggingItem: IThreatPreventionPolicyItem,
    endItem: IThreatPreventionPolicyItem | null
  ) => {
    createToast({
      type: "info",
      message: t("table.notification.changePriority.start.title"),
      detail: t("table.notification.changePriority.start.message"),
    });
    endpointService
      .changeThreatPolicyPriority(workspaceId, {
        id: draggingItem.id,
        swap_id: endItem ? endItem.id : null,
      })
      .then(() => {
        createToast({
          type: "success",
          message: t("table.notification.changePriority.success.title"),
        });
        updateThreatPolicyList();
      })
      .catch(apiErrorHandler);
  };

  // --------------- EFFECTS ---------------

  // --------------- RENDER ---------------

  return (
    <>
      <div className="pt-5 pb-6 flex flex-col gap-5 min-h-screen">
        {/* Header (Breadcrumb + title) */}
        <div className="px-8 flex flex-col gap-4">
          <BreadcrumbV2
            levels={[
              {
                name: t("breadcrumb.levels.endpoint"),
                path: generatePath(pathname.ENDPOINT_OVERVIEW, { workspaceId }),
              },
            ]}
            currentPage={{
              name: t("breadcrumb.current.threatPrevention"),
              tab: t("breadcrumb.current.ips"),
            }}
          />
          <div className="flex justify-between gap-4">
            <span className="text-display-xs font-semibold text-gray-v2-900">
              {t("title.ips")}
            </span>
            <ButtonV2
              size="md"
              onClick={() => {
                setOpenAddPolicy(true);
              }}
            >
              <Plus className="h-5 w-5 min-w-5" />
              {t("button.newIpCidrPolicy")}
            </ButtonV2>
          </div>
        </div>
        {/* Header (Breadcrumb + title) end */}

        <div className="px-8 flex flex-col gap-4 flex-1">
          <div className="flex justify-between items-center gap-2">
            {/* Filters */}
            <div className="flex gap-2">
              <FilterDropdownV2 {...actionFilterRenderProps} />
              <FilterDropdownV2 {...controlDirectionsFilterRenderProps} />
              <FilterDropdownV2 {...appliedTagsFilterRenderProps} />
              <FilterDropdownV2 {...controlStatusFilterRenderProps} />
            </div>
            {/* Filters end */}

            <div className="flex gap-2 v2">
              {/* Bulk actions */}
              {/* Bulk actions end  */}

              {/* Searchbox */}
              <SearchboxV2
                value={searchKeyword}
                onChange={(value) => {
                  setSearchKeyword(value);
                }}
                placeholder={t("table.filter.searchbox.placeholder")}
              />
              {/* Searchbox end */}
            </div>
          </div>

          {/* Table */}
          <TableDndV2<IThreatPreventionPolicyItem>
            data={threatPolicyList ?? []}
            getRowKey={(item) => item.id.toString()}
            isLoading={isLoading}
            columns={[
              {
                title: t("table.header.priority"),
                render: (_, index) => index + 1,
                gridTemplateValue: "1fr",
                titleAlign: "center",
                overwriteWrapperProps: { className: "justify-center" },
              },
              {
                title: t("table.header.ipCidr"),
                render: (item) => (
                  <>
                    {item.matches.length > 0 ? (
                      <BadgeV2 name={item.matches[0]} color="gray" />
                    ) : null}
                    {item.matches.length > 1 ? (
                      <BadgeV2 name={item.matches[1]} color="gray" />
                    ) : null}
                    <BadgeSeeMoreV2
                      badges={item.matches.slice(2).map((badge) => (
                        <BadgeV2 key={badge} name={badge} color="gray" />
                      ))}
                    />
                  </>
                ),
                gridTemplateValue: "2fr",
                overwriteWrapperProps: {
                  className: "flex-wrap gap-x-1 gap-y-0.5 py-[0.1875rem]",
                },
              },
              {
                title: t("table.header.action"),
                render: (item) =>
                  item.action.includes("block")
                    ? t("table.policyAction.block")
                    : item.action.includes("allow")
                      ? t("table.policyAction.allow")
                      : null,
                gridTemplateValue: "1fr",
              },
              {
                title: t("table.header.directions"),
                render: (item) => (
                  <>
                    {item.sources.length > 0 ? (
                      <BadgeV2
                        name={
                          item.sources[0] === ControlDirection.INBOUND
                            ? t("table.direction.inbound")
                            : item.sources[0] === ControlDirection.OUTBOUND
                              ? t("table.direction.outbound")
                              : "---"
                        }
                        color="gray"
                      />
                    ) : null}
                    {item.sources.length > 1 ? (
                      <BadgeV2
                        name={
                          item.sources[1] === ControlDirection.INBOUND
                            ? t("table.direction.inbound")
                            : item.sources[1] === ControlDirection.OUTBOUND
                              ? t("table.direction.outbound")
                              : "---"
                        }
                        color="gray"
                      />
                    ) : null}
                    <BadgeSeeMoreV2
                      badges={item.sources.slice(2).map((d) => (
                        <BadgeV2
                          key={d}
                          name={
                            d === ControlDirection.INBOUND
                              ? t("table.direction.inbound")
                              : d === ControlDirection.OUTBOUND
                                ? t("table.direction.outbound")
                                : "---"
                          }
                          color="gray"
                        />
                      ))}
                    />
                  </>
                ),
                gridTemplateValue: "2fr",
                overwriteWrapperProps: {
                  className: "flex-wrap gap-x-1 gap-y-0.5 py-[0.1875rem]",
                },
              },
              {
                title: t("table.header.appliedTags"),
                render: (item) => {
                  const tagList = item.hosts_tags
                    .filter((item) => item.type === DeviceTagType.TAG)
                    .map((item) => item.name);
                  return (
                    <>
                      {tagList.length > 0 ? (
                        <BadgeV2 name={tagList[0]} defaultIcon />
                      ) : null}
                      {tagList.length > 1 ? (
                        <BadgeV2 name={tagList[1]} defaultIcon />
                      ) : null}
                      <BadgeSeeMoreV2
                        badges={tagList.slice(2).map((tag) => (
                          <BadgeV2 key={tag} name={tag} defaultIcon />
                        ))}
                      />
                    </>
                  );
                },
                gridTemplateValue: "2fr",
                overwriteWrapperProps: { className: "gap-x-1 gap-y-0.5" },
              },
              {
                title: t("table.header.appliedDevices"),
                render: (item) => {
                  const deviceList = item.hosts_tags
                    .filter((item) => item.type === DeviceTagType.HOST)
                    .map((item) => item.name);
                  return (
                    <>
                      {deviceList.length > 0 ? (
                        <BadgeV2
                          name={deviceList[0]}
                          color="gray"
                          maxWidth={9}
                        />
                      ) : null}
                      {deviceList.length > 1 ? (
                        <BadgeV2
                          name={deviceList[1]}
                          color="gray"
                          maxWidth={9}
                        />
                      ) : null}
                      <BadgeSeeMoreV2
                        badges={deviceList.slice(2).map((device) => (
                          <BadgeV2
                            key={device}
                            name={device}
                            color="gray"
                            maxWidth={9}
                          />
                        ))}
                      />
                    </>
                  );
                },
                gridTemplateValue: "2fr",
                overwriteWrapperProps: {
                  className: "flex-wrap gap-x-1 gap-y-0.5 py-[0.1875rem]",
                },
              },
              {
                title: t("table.header.status"),
                render: (item) => (
                  <Switch
                    checked={item.enabled}
                    onClick={() => {
                      onToggleStatus(item);
                    }}
                  />
                ),
                gridTemplateValue: "1fr",
              },
              {
                title: t("table.header.note"),
                render: (item) => (
                  <span className="line-clamp-2 text-xs">
                    {item.description}
                  </span>
                ),
                gridTemplateValue: "3fr",
                overwriteWrapperProps: { className: "py-2" },
              },
              {
                title: null,
                render: (item) => {
                  const actions = getTableActions(item);
                  const disabled = actions.length === 0;

                  return (
                    <Dropdown
                      menu={{
                        items: actions.map((item) => ({
                          key: item.key,
                          label: item.label,
                        })),
                        onClick: ({ key, domEvent }) => {
                          domEvent.preventDefault();
                          domEvent.stopPropagation();
                          actions
                            .find((action) => action.key === key)
                            ?.action();
                        },
                      }}
                      dropdownRender={(menu) => (
                        <DropdownRenderV2>{menu}</DropdownRenderV2>
                      )}
                      trigger={["click"]}
                      disabled={disabled}
                    >
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      >
                        <DotsVertical
                          className={`h-5 w-5 ${disabled ? "text-gray-v2-200" : "text-gray-v2-400"} transition-colors duration-300`}
                        />
                      </button>
                    </Dropdown>
                  );
                },
                overwriteWrapperProps: { className: "justify-center" },
                gridTemplateValue: "1fr",
              },
            ]}
            options={{
              fitPageHeight: true,
            }}
            onDragEnd={onSwapPriority}
          />
          {/* Table end */}
        </div>
      </div>
      <ConfirmDeleteThreatPreventionPolicyModal
        open={confirmDeleteItem !== null}
        onChangeOpen={(value) => {
          if (!value) {
            setConfirmDeleteItem(null);
          }
        }}
        workspaceId={workspaceId}
        data={confirmDeleteItem}
        updateThreatPolicyList={updateThreatPolicyList}
      />
      <EditIpPolicyModal
        open={editingItem !== null}
        onClose={() => {
          setEditingItem(null);
        }}
        workspaceId={workspaceId}
        updatePolicyList={updateThreatPolicyList}
        editingItem={editingItem}
      />
      <AddIpPolicyModal
        open={openAddPolicy}
        onClose={() => {
          setOpenAddPolicy(false);
        }}
        workspaceId={workspaceId}
        updatePolicyList={updateThreatPolicyList}
      />
    </>
  );
};
